import { QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, type RouteObject } from 'react-router';
import { AppError } from '../api/errors';
import { queryClient } from '../data/queries';
import { I18nProvider } from '../l10n/i18n';
import { LoginPage } from '../ui/auth/LoginPage';
import { EmployeeAvatar } from '../ui/core/widgets/EmployeeAvatar';
import { ErrorView } from '../ui/core/widgets/common';
import { ToastProvider } from '../ui/core/widgets/toasts';
import { HistoryPage } from '../ui/history/HistoryPage';
import { InboxPage } from '../ui/inbox/InboxPage';
import { ItemPage } from '../ui/item/ItemPage';
import { SettingsPage } from '../ui/settings/SettingsPage';
import { EmployeePage } from '../ui/team/EmployeePage';
import { HireFormPage } from '../ui/team/HireFormPage';
import { HirePage } from '../ui/team/HirePage';
import { TeamPage } from '../ui/team/TeamPage';
import { AppShell } from './AppShell';
import { AuthProvider, useAuth } from './auth';
import { PrefsProvider, usePrefs, type PrefsOptions } from './prefs';
import { Routes } from './routes';
import { ShellExtrasProvider, type ShellExtras } from './shellExtras';

export function Splash() {
  return (
    <div className="splash">
      <EmployeeAvatar avatar="secretary" color="#8E4EC6" size={72} animate />
    </div>
  );
}

/** Signed-out people only see sign-in; a deep link to an item survives it via `from`. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status, restore } = useAuth();
  const location = useLocation();
  switch (status) {
    case 'unknown':
      return <Splash />;
    case 'offline':
      return (
        <div className="splash">
          <ErrorView error={new AppError('network')} onRetry={() => void restore()} />
        </div>
      );
    case 'signedOut': {
      const from = location.pathname + location.search;
      return <Navigate to={`${Routes.login}?from=${encodeURIComponent(from)}`} replace />;
    }
    default:
      return children;
  }
}

/** The app's pages. `login` replaces the sign-in page (the demo shows its own). */
export function appRoutes({ login = <LoginPage /> }: { login?: ReactNode } = {}): RouteObject[] {
  return [
    { path: Routes.login, element: login },
    {
      element: (
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <Navigate to={Routes.inbox} replace /> },
        { path: 'inbox', element: <InboxPage /> },
        { path: 'inbox/:itemId', element: <InboxPage /> },
        { path: 'team', element: <TeamPage /> },
        { path: 'team/employees/:id', element: <EmployeePage /> },
        { path: 'team/hire', element: <HirePage /> },
        { path: 'team/hire/:role', element: <HireFormPage /> },
        { path: 'history', element: <HistoryPage /> },
        { path: 'settings', element: <SettingsPage /> },
        { path: 'items/:id', element: <ItemPage /> },
        { path: '*', element: <Navigate to={Routes.inbox} replace /> },
      ],
    },
  ];
}

export type AppRouter = ReturnType<typeof createBrowserRouter>;

/** The router. The demo mounts the same pages under `/demo` (`basename`). */
export function createAppRouter(opts: { basename?: string; login?: ReactNode } = {}): AppRouter {
  return createBrowserRouter(appRoutes(opts), { basename: opts.basename });
}

// Made on first render, not at import: the demo imports this module and brings its own router.
let defaultRouter: AppRouter | undefined;

function Localized({ children }: { children: ReactNode }) {
  const { locale } = usePrefs();
  return <I18nProvider locale={locale}>{children}</I18nProvider>;
}

export interface AppProps {
  /** The real app's router when left out. */
  router?: AppRouter;
  prefs?: PrefsOptions;
  /** Additions to the frame around the pages (the demo's guide). */
  shell?: ShellExtras;
}

const noExtras: ShellExtras = {};

export function App({ router, prefs, shell = noExtras }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <PrefsProvider {...prefs}>
        <Localized>
          <ToastProvider>
            <AuthProvider>
              <ShellExtrasProvider value={shell}>
                <RouterProvider router={router ?? (defaultRouter ??= createAppRouter())} />
              </ShellExtrasProvider>
            </AuthProvider>
          </ToastProvider>
        </Localized>
      </PrefsProvider>
    </QueryClientProvider>
  );
}
